(ns client-portal.client.ai-agent.guide
  "In-builder guide for the product being authored against, fetched from
   /api/ai-agent/report-builder-guide — the same sources the builder's prompt is
   rendered from, so it always matches what the model can author. The server decides
   which tabs a product's guide has (its overview, the generic component catalog, a
   tab per core data it declares); this ns renders one tab per entry, dispatching on
   `:kind`. Rendered as the Guide view of the report-builder chat panel."
  (:require [client-portal.client.ai-agent.chat.data :as chat.d]
            [client-portal.client.ai-agent.data :as data]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.products.data :as products.d]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(def guide-path (conj data/report-builder-path :guide))

;; Cached per concept AND product — both change what the guide holds.
(defn- guide-cache-path [db]
  (conj guide-path (concepts.d/current-concept-id db) (chat.d/report-product db)))

(re/reg-auth0-loader
  :ai-agent/load-report-builder-guide
  (fn [_ db]
    (guide-cache-path db))
  (fn [db _]
    (let [concept-id (concepts.d/current-concept-id db)
          product (chat.d/report-product db)]
      (when (and concept-id (not-empty product))
        {:method :get
         :url "/api/ai-agent/report-builder-guide"
         :query-params (merge {:concept-id concept-id
                               :product product}
                              (products.d/project-params db [product]))})))
  :transf #(some-> % :body (util/->kebab-case-keywords))
  :error-transf (fn [] {:error? true}))

(re/reg-auth0-load-sub
  :ai-agent/report-builder-guide-
  (fn [_ db]
    (guide-cache-path db))
  :ai-agent/load-report-builder-guide)

(def ^:private mono-font "ui-monospace, SFMono-Regular, Menlo, monospace")

(defn- code-chip [text]
  [:span {:style {:background-color (style/alc-color "Background/secondary")
                  :border-radius 4
                  :color (style/alc-color "Text/primary")
                  :font-family mono-font
                  :font-size 12
                  :line-height 1.5
                  :padding "1px 6px"}}
   text])

(defn- body-text [text]
  [:div {:style {:color (style/alc-color "Text/primary")
                 :font-size 13
                 :line-height 1.45}}
   text])

(defn- detail-text [text & [style]]
  [:div {:style (merge {:color (style/alc-color "Text/secondary")
                        :font-size 12
                        :line-height 1.4}
                       style)}
   text])

(defn- section-card [& contents]
  (into [:div {:style {:border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                       :border-radius 8
                       :display :flex
                       :flex-direction :column
                       :gap 6
                       :padding "14px 16px"}}]
        (filterv identity contents)))

(defn- section-title [text & [detail]]
  [:div {:style {:display :flex
                 :align-items :center
                 :gap 8}}
   [:span {:style {:color (style/alc-color "Text/primary_header")
                   :font-family mono-font
                   :font-size 14
                   :font-weight 600}}
    text]
   (when (not-empty detail)
     [detail-text detail])])

(defn- prop-item
  "One prop. An object prop's own fields (its `:props`) render as a nested, indented
   list under it — see the catalog ns's object-props note."
  [{:keys [name type required? desc enums default props]}]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :gap 6
                 :padding "8px 0"}}
   [:div {:style {:display :flex
                  :align-items :center
                  :gap 8}}
    [code-chip name]
    [detail-text type]
    (when required?
      [:span {:style {:color (style/alc-color "Common/active")
                      :font-size 11
                      :font-weight 600
                      :letter-spacing "0.5px"
                      :text-transform :uppercase}}
       "Required"])]
   [body-text (str desc
                   (when (not-empty enums)
                     (str " One of: " (s/join ", " enums) "."))
                   (when (some? default)
                     (str " Default: " (pr-str default) ".")))]
   (when (not-empty props)
     (into [:div {:style {:border-left (str "1px solid " (style/alc-color "Border/divider_secondary"))
                          :display :flex
                          :flex-direction :column
                          :margin-left 2
                          :padding-left 10}}]
           (mapv (fn [prop] [prop-item prop]) props)))])

(defn- props-block [props]
  (when (not-empty props)
    (into [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                         :display :flex
                         :flex-direction :column
                         :margin-top 4
                         :padding-top 4}}]
          (mapv (fn [prop] [prop-item prop]) props))))

(defn- component-section [{:keys [type summary use-when props notes]}]
  [section-card
   [section-title (util/->capitalize-words type)]
   [body-text summary]
   #_(when (not-empty use-when)
       [detail-text (str "Use when: " use-when)])
   (when (not-empty notes)
     [detail-text notes {:white-space :pre-line}])
   [props-block props]])

(defn- chart-type-section [{:keys [chart-type summary props data notes]}]
  [section-card
   [section-title chart-type summary]
   (when (not-empty data)
     [detail-text (str "Data: " data)])
   (when (not-empty notes)
     [detail-text notes {:white-space :pre-line}])
   [props-block props]])

(defn- components-guide [{:keys [components common-props chart-types]}]
  (if (and (empty? components) (empty? common-props) (empty? chart-types))
    [detail-text "No components match your search."]
    (into [:div {:style {:display :flex
                         :flex-direction :column
                         :gap 12}}]
          (concat (mapv (fn [component]
                          [component-section component]) components)
                  (when (or (not-empty common-props) (not-empty chart-types))
                    [[:div {:style {:color (style/alc-color "Text/primary_header")
                                    :font-size 15
                                    :font-weight 600
                                    :margin-top 8}}
                      "chart-card chart types"]])
                  (when (not-empty common-props)
                    [[section-card
                      [section-title "Common props" "any chartType"]
                      [props-block common-props]]])
                  (mapv (fn [chart-type] [chart-type-section chart-type]) chart-types)))))

(defn- tag-value-item [{:keys [value description valid-speakers]}]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :align-items :baseline
                 :gap 6
                 :padding "3px 0"}}
   [:div {:style {:flex "0 0 auto"}}
    [code-chip value]]
   (when (or (not-empty description) (not-empty valid-speakers))
     [body-text (str description
                     (when (not-empty valid-speakers)
                       (str (when (not-empty description) " ")
                            "(speakers: " (s/join ", " valid-speakers) ")")))])])

(defn- tag-child-item [{:keys [name type description values]}]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :gap 2
                 :padding "4px 0"}}
   [:div {:style {:display :flex
                  :align-items :center
                  :gap 8}}
    [code-chip name]
    (when (not-empty type)
      [detail-text type])]
   (when (not-empty description)
     [body-text description])
   (when (not-empty values)
     (let [shown (take 30 values)
           more (- (count values) (count shown))]
       [detail-text (str "Values: " (s/join ", " shown)
                         (when (pos? more)
                           (str " … +" more " more")))]))])

(defn- tag-section [{:keys [name type values children]}]
  [section-card
   [section-title name type]
   (if (not-empty values)
     (into [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 10}}]
           (mapv (fn [value] [tag-value-item value]) values))
     [detail-text "No values."])
   (when (not-empty children)
     (into [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                          :display :flex
                          :flex-direction :column
                          :margin-top 4
                          :padding-top 8}}
            [detail-text "Children" {:font-weight 600}]]
           (mapv (fn [child] [tag-child-item child]) children)))])

(defn- chip-block
  "Labelled row of code chips. `wrap?` flows them side by side — right for short values,
   wrong for paths long enough to need a line each."
  [label chips wrap?]
  (when (not-empty chips)
    (into [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                         :display :flex
                         :flex-direction (if wrap? :row :column)
                         :flex-wrap (if wrap? :wrap :nowrap)
                         :gap 6
                         :margin-top 4
                         :padding-top 8}}
           [:div {:style {:flex "1 0 100%"}}
            [detail-text label {:font-weight 600}]]]
          (mapv (fn [chip]
                  [:div {:style {:flex "0 0 auto"}}
                   [code-chip chip]])
                chips))))

(defn- custom-metric-section [{:keys [code type value-column json-paths known-values]}]
  [section-card
   [section-title code type]
   [:div {:style {:display :flex
                  :align-items :center
                  :gap 8}}
    [detail-text "Read from"]
    [code-chip value-column]]
   [chip-block "JSON paths" json-paths false]
   [chip-block "Known Values" known-values true]])

(defn- general-guide [{:keys [paragraphs]}]
  (into [:div {:style {:display :flex
                       :flex-direction :column
                       :gap 12}}]
        (mapv (fn [paragraph] [body-text paragraph]) paragraphs)))

(defn- tags-guide [{:keys [tags empty-text]}]
  (if (not-empty tags)
    (into [:div {:style {:display :flex
                         :flex-direction :column
                         :gap 12}}]
          (mapv (fn [tag] [tag-section tag]) tags))
    [detail-text empty-text]))

(defn- custom-metrics-guide [{:keys [custom-metrics empty-text]}]
  (if (not-empty custom-metrics)
    (into [:div {:style {:display :flex
                         :flex-direction :column
                         :gap 12}}]
          (mapv (fn [metric] [custom-metric-section metric]) custom-metrics))
    [detail-text empty-text]))

(defn- matches?
  "Case-insensitive substring match of `query` against any of `texts`."
  [query texts]
  (let [q (s/lower-case query)]
    (boolean (some #(and % (s/includes? (s/lower-case (str %)) q)) texts))))

(defn- prop-texts [{:keys [name type desc enums props]}]
  (into (into [name type desc] enums)
        (mapcat prop-texts props)))

(defn- component-matches? [query {:keys [type summary use-when notes props]}]
  (matches? query (into [type summary use-when notes] (mapcat prop-texts props))))

(defn- chart-type-matches? [query {:keys [chart-type summary data notes props]}]
  (matches? query (into [chart-type summary data notes] (mapcat prop-texts props))))

(defn- tag-matches? [query {:keys [name type values children]}]
  (matches? query (concat [name type]
                          (mapcat (fn [{:keys [value description valid-speakers]}]
                                    (into [value description] valid-speakers))
                                  values)
                          (mapcat (fn [{:keys [name type description values]}]
                                    (into [name type description] values))
                                  children))))

(defn- custom-metric-matches? [query {:keys [code type value-column json-paths known-values]}]
  (matches? query (concat [code type value-column] json-paths known-values)))

(defn- tab-body [guide query {:keys [kind] :as tab}]
  (let [common-props (:chart-card-common-props guide)]
    (case kind
      "general"
      [general-guide tab]

      "components"
      [components-guide
       {:components (cond->> (:components guide)
                             query (filterv #(component-matches? query %)))
        :common-props (when (or (not query)
                                (matches? query (into ["chart-card"]
                                                      (mapcat prop-texts common-props))))
                        common-props)
        :chart-types (cond->> (:chart-card-chart-types guide)
                              query (filterv #(chart-type-matches? query %)))}]

      "tags"
      [tags-guide
       {:tags (cond->> (:tags tab)
                       query (filterv #(tag-matches? query %)))
        :empty-text (if query
                      "No tags match your search."
                      "No tags are configured for this concept.")}]

      "custom-metrics"
      [custom-metrics-guide
       {:custom-metrics (cond->> (:custom-metrics tab)
                                 query (filterv #(custom-metric-matches? query %)))
        :empty-text (if query
                      "No custom metrics match your search."
                      "No custom metrics are recorded for this client.")}]

      nil)))

(defn- search-placeholder
  "Names the core-data tabs this product actually has, so the input doesn't offer to
   search something the guide isn't showing."
  [tabs]
  (let [searchable (keep {"tags" "tags"
                          "custom-metrics" "custom metrics"}
                         (map :kind tabs))]
    (if (not-empty searchable)
      (str "Search components, props, and " (s/join ", " searchable))
      "Search components and props")))

(defn- guide-tabs
  "One product's guide: a tab per entry in `:tabs` (its overview, the component
   catalog, its core-data tabs), filtered by the search input. Rendered keyed by
   product (see `guide-content`), so switching products remounts it — the selected
   tab and the search reset instead of holding a tab the new product doesn't have."
  [_]
  (let [search* (re/ratom nil)]
    (fn [guide]
      (let [query (some-> @search* (s/trim) (not-empty))
            tabs (:tabs guide)]
        (into [alc/up-tabs-root {:default-value (:value (first tabs))}
               [:div {:style {:background-color (style/alc-color "Background/card_hex")
                              :display :flex
                              :flex-direction :column
                              :gap 12
                              :margin "0 -10px"
                              :padding "8px 10px 0"
                              :position :sticky
                              :top 0
                              :z-index (style/z :medium)}}
                [alc/up-input {:placeholder (search-placeholder tabs)
                               :on-change #(reset! search* (.. % -target -value))}]
                (into [alc/up-tabs-list {}]
                      (mapv (fn [{:keys [value label]}]
                              ^{:key value}
                              [alc/up-tabs-trigger {:value value} label])
                            tabs))]]
              (mapv (fn [{:keys [value] :as tab}]
                      ^{:key value}
                      [alc/up-tabs-content {:value value}
                       [:div {:style {:margin-top 12}}
                        [tab-body guide query tab]]])
                    tabs))))))

(defn guide-content
  "The Guide view's body — the guide for the product being authored against, with
   loading and retry states. Scrolls in whatever container it's in. Both the fetch
   (see `guide-cache-path`) and the rendered tabs are keyed by product, so switching
   products swaps the whole guide rather than restyling the previous one."
  []
  (let [guide- (re/subscribe [:ai-agent/report-builder-guide-])
        product- (re/subscribe [:ai-agent/report-product-])]
    (fn []
      (let [guide @guide-]
        [:div {:style {:padding "16px 10px 30px"}}
         (cond
           (or (nil? guide) (util/loading? guide))
           [:div {:style {:display :flex
                          :justify-content :center
                          :padding "60px 0"}}
            [alc/up-loader-with-label {:label "Loading Guide..."}]]

           (:error? guide)
           [:div {:style {:display :flex
                          :flex-direction :column
                          :align-items :center
                          :gap 12
                          :padding "40px 0"}}
            [body-text "Failed to load the guide."]
            [alc/up-button {:on-click #(re/dispatch [:ai-agent/load-report-builder-guide :reload])}
             "Try Again"]]

           :else
           ^{:key @product-}
           [guide-tabs guide])]))))
